import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Heart, Activity, Star, Clock, Users, AlertTriangle } from 'lucide-react';

interface WellnessMetric {
  category: string;
  score: number;
  trend: number;
  status: 'IMPROVING' | 'STABLE' | 'DECLINING';
  insights: string[];
}

interface WellnessTrend {
  date: string;
  overall: number;
  physical: number;
  cognitive: number;
  social: number;
  sleep: number;
}

const WellnessScore: React.FC = () => {
  // Mock data - in real app this would be calculated from various sensor inputs
  const metrics: WellnessMetric[] = [
    {
      category: 'Physical Health',
      score: 85,
      trend: 2,
      status: 'IMPROVING',
      insights: [
        'Regular daily exercise routine maintained',
        'Stable gait patterns observed',
        'Good medication adherence'
      ]
    },
    {
      category: 'Cognitive Function',
      score: 82,
      trend: -1,
      status: 'STABLE',
      insights: [
        'Consistent daily routine adherence',
        'Slight decrease in evening navigation',
        'Good problem-solving engagement'
      ]
    },
    {
      category: 'Social Engagement',
      score: 88,
      trend: 3,
      status: 'IMPROVING',
      insights: [
        'Increased group activity participation',
        'Regular family interactions',
        'Active community involvement'
      ]
    },
    {
      category: 'Sleep Quality',
      score: 78,
      trend: -2,
      status: 'DECLINING',
      insights: [
        'Increased nighttime restlessness',
        'More frequent bathroom visits',
        'Irregular sleep schedule'
      ]
    }
  ];

  const trendData: WellnessTrend[] = [
    { date: 'Week 1', overall: 80, physical: 82, cognitive: 79, social: 83, sleep: 76 },
    { date: 'Week 2', overall: 82, physical: 83, cognitive: 80, social: 85, sleep: 77 },
    { date: 'Week 3', overall: 81, physical: 84, cognitive: 81, social: 86, sleep: 75 },
    { date: 'Week 4', overall: 83, physical: 85, cognitive: 82, social: 88, sleep: 78 }
  ];

  const overallScore = {
    current: 83,
    trend: 2,
    status: 'IMPROVING' as const,
    riskLevel: 'LOW' as const
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return '↑';
    if (trend < 0) return '↓';
    return '→';
  };

  const getTrendColor = (trend: number) => {
    if (trend > 0) return 'text-green-500';
    if (trend < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Physical Health':
        return <Activity className="text-blue-500" />;
      case 'Cognitive Function':
        return <Star className="text-purple-500" />;
      case 'Social Engagement':
        return <Users className="text-green-500" />;
      case 'Sleep Quality':
        return <Clock className="text-indigo-500" />;
      default:
        return <Heart className="text-red-500" />;
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Wellness Score</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 col-span-1 md:col-span-2 lg:col-span-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Heart className="text-red-500 mr-2" size={32} />
              <div>
                <h3 className="text-xl font-semibold">Overall Wellness Score</h3>
                <p className="text-sm text-gray-500">Comprehensive health assessment</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-4xl font-bold ${getScoreColor(overallScore.current)}`}>
                {overallScore.current}
              </p>
              <p className={`text-sm ${getTrendColor(overallScore.trend)}`}>
                {getTrendIcon(overallScore.trend)} {Math.abs(overallScore.trend)}% ({overallScore.status.toLowerCase()})
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Wellness Categories</h3>
          <div className="space-y-6">
            {metrics.map((metric, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  {getCategoryIcon(metric.category)}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{metric.category}</span>
                    <div className="text-right">
                      <span className={`font-bold ${getScoreColor(metric.score)}`}>
                        {metric.score}
                      </span>
                      <span className={`ml-2 text-sm ${getTrendColor(metric.trend)}`}>
                        {getTrendIcon(metric.trend)} {Math.abs(metric.trend)}%
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    {metric.insights.map((insight, i) => (
                      <p key={i} className="text-sm text-gray-600">• {insight}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Wellness Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis domain={[60, 100]} />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="overall" 
                name="Overall Score"
                stroke="#ef4444"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="physical" 
                name="Physical Health"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="cognitive" 
                name="Cognitive"
                stroke="#8b5cf6"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="social" 
                name="Social"
                stroke="#10b981"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="sleep" 
                name="Sleep"
                stroke="#6366f1"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Wellness Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Activity className="text-green-500" />
              </div>
              <div>
                <h4 className="font-medium">Physical Activity</h4>
                <p className="text-gray-600">Consistent exercise routine and stable gait patterns indicate good physical health maintenance.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Star className="text-purple-500" />
              </div>
              <div>
                <h4 className="font-medium">Cognitive Health</h4>
                <p className="text-gray-600">Strong adherence to daily routines suggests stable cognitive function, with minor evening navigation concerns.</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Users className="text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium">Social Wellbeing</h4>
                <p className="text-gray-600">Increased participation in group activities and community events shows improving social engagement.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="text-yellow-500" />
              </div>
              <div>
                <h4 className="font-medium">Areas for Attention</h4>
                <p className="text-gray-600">Sleep quality shows slight decline. Consider sleep hygiene assessment and evening routine adjustments.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessScore; 